import { Component, OnDestroy, OnInit } from '@angular/core';
import { ITEMS_PER_PAGE, Principal } from '../../shared';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { TransactionSearch } from './transaction-search.model';
import { ColumnFilterComponent, FilterType } from '../../shared/column-filter/column-filter.component';
import { InventoryUnit } from '../../inventory-manager/inventory-unit.model';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MANAGE_TRANSACTIONS_PERMISSIONS, VIEW_NAMES_PERMISSION } from '../../shared/auth/permissions';
import { SearchComposite } from '../../shared/model/search-composite';
import { InventoryContext } from '../../inventory-manager/inventory.context';
import { finalize } from 'rxjs/operators';
import { SearchResult } from '../../shared/search-result.model';
import { GermplasmTreeTableComponent } from '../../shared/tree/germplasm/germplasm-tree-table.component';
import { StudyTreeComponent } from '../../shared/tree/study/study-tree.component';
import { AlertService } from '../../shared/alert/alert.service';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { FileDownloadHelper } from '../sample/file-download.helper';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { TransactionService } from '../../shared/inventory/service/transaction.service';
import { LotDetailPopupService } from '../lot-detail/lot-detail-popup.service';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { Transaction, TransactionStatus } from '../../shared/inventory/model/transaction.model';
import { TransactionType } from '../../shared/inventory/service/transaction-type.model';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { preventDefaultOnShiftKey } from '../../field-management/utils';
import { TranslateService } from '@ngx-translate/core';
import { ColumnFilterRadioButtonOption } from '../../shared/column-filter/column-filter-radio-component';

@Component({
    selector: 'jhi-transaction',
    templateUrl: './transaction.component.html'
})
export class TransactionComponent implements OnInit, OnDestroy {

    CONFIRM_TRANSACTIONS_PERMISSION = [...MANAGE_TRANSACTIONS_PERMISSIONS, 'CONFIRM_TRANSACTIONS'];
    IMPORT_TRANSACTION_UPDATES_PERMISSION = [...MANAGE_TRANSACTIONS_PERMISSIONS, 'IMPORT_TRANSACTION_UPDATES'];
    CANCEL_PENDING_TRANSACTIONS_PERMISSION = [...MANAGE_TRANSACTIONS_PERMISSIONS, 'CANCEL_PENDING_TRANSACTIONS'];
    VIEW_NAMES_PERMISSION = VIEW_NAMES_PERMISSION;

    TOOLTIP_MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;

    transactions: Transaction[];
    error: any;
    success: any;
    currentSearch: string;
    routeData: any;
    links: any;
    totalItems: any;
    queryCount: any;
    itemsPerPage: any;
    page: any;
    predicate: any;
    previousPage: any;
    reverse: boolean;
    searchResult: SearchResult;
    lastClickIndex: any;
    subscriptionsManager = new SubscriptionsManager();

    isLoading: boolean;
    isExportingTemplate: boolean;

    get request() {
        return this.inventoryContext.transactionSearchRequest;
    }

    set request(request) {
        this.inventoryContext.transactionSearchRequest = request;
    }

    get filters() {
        return this.inventoryContext.transactionFilters;
    }

    set filters(filters) {
        this.inventoryContext.transactionFilters = filters;
    }

    get hiddenColumns() {
        return this.inventoryContext.transactionHiddenColumns;
    }

    set hiddenColumns(hiddenColumns) {
        this.inventoryContext.transactionHiddenColumns = hiddenColumns;
    }

    units: Promise<InventoryUnit[]>;
    transactionTypes: Promise<TransactionType[]>;
    transactionStatus: Promise<TransactionStatus[]>;

    selectedItems: any[] = [];
    allItemsPerPages = false;

    private getInitialFilters(units, transactionTypes, transactionStatus) {
        return [
            {
                key: 'lotStatus', name: 'column-filter.lot.status', default: true, defaultValue: 0, type: FilterType.RADIOBUTTON,
                value: undefined,
                options: this.getStatusFilterOptions()
            },
            { key: 'lotIds', name: 'column-filter.lot.id', placeholder: 'column-filter.comma.separated.values.placeholder', default: true, type: FilterType.LIST },
            { key: 'transactionIds', name: 'column-filter.transaction.id', placeholder: 'column-filter.comma.separated.values.placeholder', default: true, type: FilterType.LIST },
            {
                key: 'transactionTypes', name: 'column-filter.transaction.type', default: true, type: FilterType.CHECKLIST,
                value: undefined,
                translate: true,
                options: transactionTypes
            },
            {
                key: 'transactionStatus', name: 'column-filter.transaction.status', default: true, type: FilterType.CHECKLIST,
                value: undefined,
                translate: true,
                options: transactionStatus
            },
            { key: 'gids', name: 'column-filter.gid', placeholder: 'column-filter.comma.separated.values.placeholder', type: FilterType.LIST },
            {
                key: 'germplasmListIds', name: 'column-filter.germplasm.list', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(GermplasmTreeTableComponent as Component, { size: 'lg', backdrop: 'static' })
                            .result.then((germplasmList) => {
                                if (germplasmList) {
                                    this.value = germplasmList.map((list) => list.name);
                                    request[this.key] = germplasmList.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            {
                key: 'plantingStudyIds', name: 'column-filter.study.of.use', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(StudyTreeComponent as Component, { windowClass: 'modal-extra-small', backdrop: 'static' })
                            .result.then((studyIds) => {
                                if (studyIds) {
                                    this.value = studyIds.map((list) => list.name);
                                    request[this.key] = studyIds.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            {
                key: 'originStudyIds', name: 'column-filter.study.of.origin', type: FilterType.MODAL,
                open(modal, request) {
                    return new Promise<void>((resolve) => {
                        modal.open(StudyTreeComponent as Component, { windowClass: 'modal-extra-small', backdrop: 'static' })
                            .result.then((studyIds) => {
                                if (studyIds) {
                                    this.value = studyIds.map((list) => list.name);
                                    request[this.key] = studyIds.map((list) => list.id);
                                }
                                resolve();
                            }, () => {
                            });
                    });
                }
            },
            { key: 'designation', name: 'column-filter.designation', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'stockId', name: 'column-filter.stock.id', placeholder: 'column-filter.starts.with.placeholder', type: FilterType.TEXT },
            { key: 'createdByUsername', name: 'column-filter.username', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'createdDate', name: 'column-filter.creation.date', type: FilterType.DATE,
                fromKey: 'createdDateFrom',
                toKey: 'createdDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetDateFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            {
                key: 'unitIds', name: 'column-filter.units', type: FilterType.CHECKLIST,
                options: units
            },
            {
                key: 'amount', name: 'column-filter.amount', type: FilterType.NUMBER,
                fromKey: 'minAmount',
                toKey: 'maxAmount',
                transform(req) {
                    ColumnFilterComponent.transformMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetMinMaxFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.min = req[this.fromKey];
                    this.max = req[this.toKey];
                }
            },
            { key: 'notes', name: 'column-filter.notes', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            { key: 'observationUnitIds', name: 'column-filter.observation.units', placeholder: 'column-filter.match.text.placeholder', type: FilterType.TEXT,
                transform(req) {
                    req[this.key] = [this.value];
                }
            }
        ];
    }

    constructor(
        private jhiLanguageService: JhiLanguageService,
        private transactionService: TransactionService,
        private alertService: AlertService,
        private principal: Principal,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private eventManager: JhiEventManager,
        private inventoryService: InventoryService,
        private modalService: NgbModal,
        private fileDownloadHelper: FileDownloadHelper,
        private inventoryContext: InventoryContext,
        public lotDetailPopupService: LotDetailPopupService,
        private translateService: TranslateService,
        public germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
        this.itemsPerPage = ITEMS_PER_PAGE;
        this.subscriptionsManager.add = this.activatedRoute.data.subscribe((data) => {
            this.page = data.pagingParams.page;
            this.previousPage = data.pagingParams.page;
            this.reverse = data.pagingParams.ascending;
            this.predicate = data.pagingParams.predicate;
        });
        this.currentSearch = this.activatedRoute.snapshot && this.activatedRoute.snapshot.params['search'] ?
            this.activatedRoute.snapshot.params['search'] : '';

        this.subscriptionsManager.add = this.activatedRoute.queryParams.subscribe((params) => {
            this.units = this.inventoryService.queryUnits().toPromise();
            this.transactionTypes = this.transactionService.queryTransactionTypes().toPromise();
            this.transactionStatus = this.transactionService.queryTransactionStatus().toPromise();
        });

        if (!this.filters) {
            this.filters = this.getInitialFilters(this.getUnits(), this.getTransactionTypes(), this.getTransactionStatus());
            if (!this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION)) {
                this.filters = this.filters.filter((filter) => {
                    return filter.key !== 'designation';
                });
            }
            this.request.lotStatus = 0;
            this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        }
        this.searchResult = new SearchResult('');
    }

    private async getStatusFilterOptions(): Promise<ColumnFilterRadioButtonOption[]> {
        await this.translateService.get('column-filter.active.option').toPromise();
        return new Promise<ColumnFilterRadioButtonOption[]>((resolve, reject) => {
            resolve([new ColumnFilterRadioButtonOption(0, 'column-filter.active.option'),
                new ColumnFilterRadioButtonOption(1, 'column-filter.closed.option')]);
        });
    }

    private resetFilters() {
        this.filters = this.getInitialFilters(this.getUnits(), this.getTransactionTypes(), this.getTransactionStatus());
        this.request = new TransactionSearch();
        this.searchResult = new SearchResult('');
    }

    /**
     * Returns a copy of the units
     */
    private getUnits() {
        return new Promise((resolve) => {
            this.units.then((units: InventoryUnit[]) => {
                resolve(units.map((unit) => <InventoryUnit>(unit)));
            });
        });
    }

    /**
     * Returns a copy of the transactionType
     */
    private getTransactionTypes() {
        return new Promise((resolve) => {
            this.transactionTypes.then((transactionTypes: TransactionType[]) => {
                resolve(transactionTypes.map((transactionType) => {
                    return {
                        id: transactionType.id,
                        name: 'inventoryManagerApp.transaction.type.' + transactionType.value
                    };
                }));
            });
        });
    }

    /**
     * Returns a copy of the transactionStatus
     */
    private getTransactionStatus() {
        return new Promise((resolve) => {
            this.transactionStatus.then((transactionStatus: TransactionStatus[]) => {
                resolve(transactionStatus.map((transactionStatus) => {
                    return {
                        id: transactionStatus.id,
                        name: 'inventoryManagerApp.transaction.status.' + transactionStatus.name
                    };
                }));
            });
        });
    }

    search(request: TransactionSearch): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!this.searchResult.searchResultDbId) {
                this.subscriptionsManager.add = this.transactionService.search(request).subscribe((response) => {
                    this.searchResult.searchResultDbId = response;
                    resolve(this.searchResult.searchResultDbId);
                }, (error) => reject(error));
                this.page = 1;
            } else {
                resolve(this.searchResult.searchResultDbId);
            }
        });
    }

    loadAll(request: TransactionSearch) {
        this.isLoading = true;
        this.search(request).then((searchId) => {
            this.subscriptionsManager.add = this.transactionService.getSearchResults({
                searchRequestId: searchId,
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.sort()
            }).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<Transaction[]>) => this.onSuccess(res.body, res.headers)
            );
        });
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    clear() {
        this.page = 0;
        this.currentSearch = '';
        this.router.navigate(['./', {
            page: this.page,
            sort: this.predicate + ',' + (this.reverse ? 'asc' : 'desc')
        }], { relativeTo: this.activatedRoute, queryParamsHandling: 'merge', });
        this.loadAll(this.request);
    }

    ngOnInit() {
        const queryParams = this.activatedRoute.snapshot && this.activatedRoute.snapshot.queryParams || {};

        // TODO Refactor to filterBy() and key/value pairs
        if (queryParams.filterBy) {
            this.resetFilters();

            this.request.lotIds = [queryParams.lotId];
            this.request.lotStatus = undefined;

            switch (queryParams.filterBy) {

            case 'lots': {
                this.loadAll(this.request);
                break;
            }

            case 'actualBalance': {
                this.request.transactionStatus = [1];
                this.loadAll(this.request);
                break;
            }

            case 'availableBalance': {
                this.subscriptionsManager.add = this.transactionService.getAvailableBalance(queryParams.lotUUID).subscribe(
                    (res: HttpResponse<Transaction[]>) => {
                        this.request.transactionIds = res.body.map((transaction) => transaction.transactionId);
                        this.request.lotIds = undefined;
                        this.loadAll(this.request);
                        this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
                    }
                );
                break;
            }
            case 'reserved': {
                this.request.transactionTypes = [1];
                this.request.transactionStatus = [0];
                this.loadAll(this.request);
                break;
            }
            case 'totalWithdrawals': {
                this.request.transactionTypes = [1];
                this.request.transactionStatus = [1];
                this.loadAll(this.request);
                break;
            }
            case 'pendingDeposits': {
                this.request.transactionTypes = [4];
                this.request.transactionStatus = [0];
                this.loadAll(this.request);
                break;
            }
            }
            this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        } else {
            this.loadAll(this.request);
        }
        this.registerChangeInTransactions();
        this.hiddenColumns['lotId'] = true;

    }

    ngOnDestroy() {
        this.subscriptionsManager.unsubscribeAll();
    }

    trackId(index: number, item: Transaction) {
        return item.transactionId;
    }

    /**
     * Filter by different fields.
     * - Some filters may have a custom filtering logic
     * - Will clear all existing filters
     * @param filterBy map with key value filters
     */
    private filterBy(filterBy: { [p: string]: any }) {
        if (!filterBy) {
            return;
        }
        const entries = Object.entries(filterBy);
        if (entries.length === 0) {
            return;
        }
        this.resetFilters();

        entries.forEach(([key, value]) => {
            switch (key) {
            case 'transactionIds': {
                this.request.transactionIds = value;
                this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
            }
                // TODO Refactor: move queryParam filterBy here
            }
        });
    }

    registerChangeInTransactions() {
        this.subscriptionsManager.add = this.eventManager.subscribe('columnFiltersChanged', (event) => {
            if (event && event.content && event.content.filterBy) {
                this.filterBy(event.content.filterBy);
                this.resetRouteFilterBy();
            }
            this.resetTable();
        });
    }

    /**
     * Clear queryParam filters: lotId, filterBy
     */
    private resetRouteFilterBy() {
        this.router.navigate([], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: { lotId: null, filterBy: null }
        });
    }

    resetTable() {
        this.allItemsPerPages = false;
        this.selectedItems = [];
        this.loadAll(this.request);
    }

    sort() {
        const result = [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
        if (this.predicate !== 'lot.lotId') {
            result.push('lot.lotId');
        }
        return result;
    }

    private onSuccess(data, headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.queryCount = this.totalItems;
        this.transactions = data;
    }

    isSelected(transaction: Transaction) {
        return transaction && this.selectedItems.length > 0 && this.selectedItems.find((item) => item === transaction.transactionId);
    }

    onSelectAllPage() {
        const allPageSelected = this.isAllPageSelected();
        const lotCurrentPage = this.transactions.map((transaction) => transaction.transactionId);
        if (allPageSelected) {
            this.selectedItems = this.selectedItems.filter((item) =>
                lotCurrentPage.indexOf(item) === -1);
        } else {
            this.selectedItems = lotCurrentPage.filter((item) =>
                this.selectedItems.indexOf(item) === -1
            ).concat(this.selectedItems);
        }
    }

    onSelectAllPages(selectAllItems) {
        this.allItemsPerPages = !selectAllItems;
        if (this.allItemsPerPages) {
            this.selectedItems = [];
        }
    }

    toggleSelect($event, index, transaction: Transaction, checkbox = false) {
        if (this.allItemsPerPages) {
            return;
        }
        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems = [];
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.transactions.slice(min, max);
        } else {
            items = [transaction];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.find((item) => item === transaction.transactionId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems = this.selectedItems.filter((item) => item !== transaction.transactionId);
            } else {
                this.selectedItems.push(item.transactionId);
            }
        }
    }

    isAllPageSelected() {
        return this.transactions.length > 0 && !this.transactions.some((transaction) => this.selectedItems.indexOf(transaction.transactionId) === -1);
    }

    openConfirmTransactionModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();

        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
        } else {
            searchComposite.itemIds = this.selectedItems;
        }

        const numOfTransactionToConfirm = this.allItemsPerPages ? this.totalItems : this.selectedItems.length;
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('inventoryManagerApp.transaction.confirm.message', { param : numOfTransactionToConfirm });
        confirmModalRef.componentInstance.title = this.translateService.instant('inventoryManagerApp.transaction.confirm.title');
        confirmModalRef.result.then(() => {
            this.subscriptionsManager.add = this.transactionService.confirmPendingTransaction(searchComposite).subscribe(
                (res: HttpResponse<any>) => {
                    return this.onSaveSuccess('inventoryManagerApp.transaction.confirm.pending.success');
                }
            );
        });
    }

    private onSaveSuccess(message: any) {
        this.alertService.success(message);
        this.resetTable();
    }

    openTransactionUpdateModal() {
        this.router.navigate(['/', { outlets: { popup: 'transaction-update' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge'
        });
    }

    exportTransactionsTemplate() {

        this.isExportingTemplate = true;
        if (this.transactions.length === 0) {
            this.alertService.error('error.filter.empty.list');
            return;
        }

        this.subscriptionsManager.add = this.transactionService.exportTransactionsTemplate({ searchRequestId: this.searchResult.searchResultDbId }).pipe((finalize(() => {
            this.isExportingTemplate = false;
        }))).subscribe((response) => {
            const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
            this.fileDownloadHelper.save(response.body, fileName);
        });

    }

    openCancelPendingTransactionModal() {
        if (!this.validateSelection()) {
            return;
        }

        const searchComposite: SearchComposite<any, any> = new SearchComposite();

        if (this.allItemsPerPages) {
            searchComposite.searchRequest = this.searchResult.searchResultDbId;
        } else {
            searchComposite.itemIds = this.selectedItems;
        }

        const numOfTransactionToCancel = this.allItemsPerPages ? this.totalItems : this.selectedItems.length;
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('inventoryManagerApp.transaction.cancel.message', { param : numOfTransactionToCancel });
        confirmModalRef.componentInstance.title = this.translateService.instant('inventoryManagerApp.transaction.cancel.title');
        confirmModalRef.result.then(() => {
            this.subscriptionsManager.add = this.transactionService.cancelPendingTransaction(searchComposite).subscribe(
                (res: HttpResponse<any>) => {
                    return this.onSaveSuccess('inventoryManagerApp.transaction.cancel.pending.success');
                }
            );
        });
    }

    private validateSelection() {
        if (this.transactions.length === 0 || (!this.allItemsPerPages && this.selectedItems.length === 0)) {
            this.alertService.error('inventoryManagerApp.transaction.select.at.least.one');
            return false;
        }
        return true;
    }

    clearSelected() {
        this.selectedItems = [];
        this.allItemsPerPages = true;
    }

    onPageSizeChange($event: Event) {
        this.loadAll(this.request);
    }

    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;
}
